import { useCallback, useEffect, useRef, useState } from 'react'
import { getEntries } from './audit-api'
import { auditErrorText } from './entry-format'

// Keyset infinite paging over GET /entries (phase 4 ruling 8). params is a
// plain object (scope and filters, already windowed). The first page names
// the window (from/to); every later page sends the cursor, which carries
// that window, plus the same filters. A response for parameters the feed
// has moved on from never lands (generation check), and only one page is
// read at a time: fetchPage's busy check is the one guard for that. A
// failed page keeps what was loaded; a cancelled read says nothing, and
// errorStatus keeps the refusal's HTTP status (403, 404) so a screen can
// say why. After unmount nothing reads and nothing updates (live ref).
//
// params is compared by value (JSON), so a caller may build it inline.
export const useAuditFeed = (params, { enabled = true } = {}) => {
  const key = JSON.stringify(params || {})
  const [state, setState] = useState({
    items: [],
    next: null,
    loading: false,
    error: null,
    errorStatus: null,
    failure: null,
    geoUnavailable: false
  })
  const live = useRef(true)
  const gen = useRef(0)
  const busy = useRef(false)
  const nextRef = useRef(null)
  const keyRef = useRef(key)
  keyRef.current = key

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const fetchPage = useCallback(async first => {
    if (!live.current) return
    // The one-page-at-a-time guard: a later page waits for the one in flight.
    if (busy.current && !first) return
    const mine = first ? ++gen.current : gen.current
    busy.current = true
    setState(s => ({
      ...s,
      loading: true,
      error: null,
      errorStatus: null,
      failure: null,
      ...(first ? { items: [], next: null, geoUnavailable: false, record: null, within: null } : {})
    }))
    const p = JSON.parse(keyRef.current)
    if (!first) {
      delete p.from
      delete p.to
      p.before = nextRef.current
    }
    try {
      const page = (await getEntries(p)) || { items: [] }
      if (!live.current || mine !== gen.current) return
      const items = page.items || []
      nextRef.current = page.next || null
      setState(s => {
        const seen = new Set(first ? [] : s.items.map(e => e.id))
        const fresh = items.filter(e => !seen.has(e.id) && seen.add(e.id))
        return {
          items: first ? fresh : [...s.items, ...fresh],
          next: page.next || null,
          loading: false,
          error: null,
          errorStatus: null,
          failure: null,
          geoUnavailable: Boolean(page.geoUnavailable) || (!first && s.geoUnavailable),
          // a record= page's first page names its record (content), for a
          // History with no entries
          record: first ? page.record || null : s.record,
          // the within filter's node, named (content's first page)
          within: first ? page.within || null : s.within
        }
      })
    } catch (err) {
      if (!live.current || mine !== gen.current) return
      const error = auditErrorText(err, first ? "The history couldn't be loaded." : "Older entries couldn't be loaded.")
      setState(s => ({ ...s, loading: false, error, errorStatus: error === null ? null : (err && err.status) || null, failure: error === null ? null : err }))
    } finally {
      if (mine === gen.current) busy.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    busy.current = false
    nextRef.current = null
    fetchPage(true)
  }, [key, enabled, fetchPage])

  const loadMore = useCallback(() => (nextRef.current ? fetchPage(false) : Promise.resolve()), [fetchPage])
  const reload = useCallback(() => fetchPage(true), [fetchPage])

  return {
    items: state.items,
    record: state.record || null,
    within: state.within || null,
    loading: state.loading,
    error: state.error,
    errorStatus: state.errorStatus,
    // the failed request behind error (its reference: shared/error)
    failure: state.failure,
    hasMore: Boolean(state.next),
    loadMore,
    reload,
    geoUnavailable: state.geoUnavailable
  }
}
